/**
 * Custom Quill Image blot that supports width (resize).
 * Must be imported and run before ReactQuill mounts.
 * Compatible with Quill 2 / Parchment 3.
 */
import Quill from "quill";
import { EmbedBlot } from "parchment";
import { BlockEmbed } from "quill/blots/block.js";

// Override default image format (extends EmbedBlot like Quill 2's Image)
class ResizableImage extends EmbedBlot {
  static blotName = "image";
  static tagName = "IMG";

  static create(value) {
    const node = super.create(value);
    const url =
      typeof value === "string" ? value : value?.url || value?.src || "";
    const width =
      typeof value === "object" && value?.width ? value.width : null;
    node.setAttribute("src", url);
    node.setAttribute("alt", "");
    node.setAttribute("class", "top-img");
    if (width) {
      node.style.width = width + "px";
      node.style.height = "auto";
    }
    node.style.maxWidth = "100%";
    node.style.cursor = "pointer";
    return node;
  }

  static value(node) {
    const width = node.style.width;
    return {
      url: node.getAttribute("src") || "",
      width: width ? parseInt(width, 10) : undefined,
    };
  }

  format(name, value) {
    if (name === "width" && this.domNode) {
      if (value) {
        this.domNode.style.width = value + "px";
        this.domNode.style.height = "auto";
      } else {
        this.domNode.style.width = "";
        this.domNode.style.height = "";
      }
    }
  }
}

Quill.register("formats/image", ResizableImage, true);

// Block embed for company logo: <div class="top-img-container"><img class="top-img" ...></div>
// Must extend BlockEmbed so insertEmbed() can create it (Quill 2 registry).
class TopImgContainer extends BlockEmbed {
  static blotName = "topImgContainer";
  static tagName = "DIV";

  static create(value) {
    const node = super.create();
    node.setAttribute("class", "top-img-container");
    const justifyContent =
      typeof value === "object" && value?.justifyContent
        ? value.justifyContent
        : "flex-start";
    node.style.justifyContent = justifyContent;
    const img = document.createElement("img");
    img.setAttribute("class", "top-img");
    const src =
      typeof value === "string" ? value : value?.src || value?.url || "";
    const style = typeof value === "object" && value?.style ? value.style : "";
    img.setAttribute("src", src);
    img.setAttribute("alt", "");
    if (style) img.setAttribute("style", style);
    node.appendChild(img);
    return node;
  }

  static value(node) {
    const img = node.querySelector("img");
    const justifyContent = node.style?.justifyContent || "";
    if (!img) return { src: "", style: "", justifyContent };
    return {
      src: img.getAttribute("src") || "",
      style: img.getAttribute("style") || "",
      justifyContent,
    };
  }
}

Quill.register("formats/topImgContainer", TopImgContainer, true);

export default ResizableImage;
export { TopImgContainer };
